// src/store/selectors/karigarSelectors.js
// ============================================================================
// KARIGAR SELECTORS - UPDATED VERSION
// ============================================================================
// Changes:
// 1. Updated currentJobPayments selectors for new structure
// 2. Added cancel loading state to payment selectors
// 3. Added removeDeduction loading state
// 4. Added new payment flow selectors (completed, pending, karigarPayment)
// ============================================================================

import { createSelector } from "@reduxjs/toolkit";

// ========== BASE SELECTORS (with safety) ==========

// Karigar state - handles nested state structure
export const selectKarigarState = (state) => state.karigar?.karigar || {};

// Job state
export const selectJobState = (state) => state.karigar?.job || {};

// Payment state
export const selectPaymentState = (state) => state.karigar?.payment || {};

// ========== KARIGAR SELECTORS ==========

export const selectKarigars = createSelector(
  [selectKarigarState],
  (karigarState) => {
    const karigars = karigarState.karigars;
    if (Array.isArray(karigars)) {
      return karigars;
    }
    if (karigars?.data && Array.isArray(karigars.data)) {
      return karigars.data;
    }
    return [];
  }
);

export const selectCurrentKarigar = createSelector(
  [selectKarigarState],
  (karigarState) => karigarState.currentKarigar || null
);

export const selectKarigarPagination = createSelector(
  [selectKarigarState],
  (karigarState) =>
    karigarState.pagination || {
      page: 1,
      limit: 20,
      total: 0,
      pages: 0,
    }
);

export const selectKarigarFilters = createSelector(
  [selectKarigarState],
  (karigarState) =>
    karigarState.filters || {
      status: "",
      branchCode: "",
      skill: "",
      search: "",
      isAvailable: "",
    }
);

export const selectKarigarLoading = createSelector(
  [selectKarigarState],
  (karigarState) =>
    karigarState.loading || {
      list: false,
      detail: false,
      create: false,
      update: false,
      delete: false,
      status: false,
      availability: false,
      bulkAvailability: false,
      stats: false,
      branch: false,
      skills: false,
      workPreferences: false,
      search: false,
      available: false,
      performanceReport: false,
      bulkApprove: false,
      bulkSuspend: false,
      bulkDelete: false,
      password: false,
      verification: false,
      estimate: false,
    }
);

export const selectKarigarError = createSelector(
  [selectKarigarState],
  (karigarState) => karigarState.error || null
);

export const selectKarigarSuccessMessage = createSelector(
  [selectKarigarState],
  (karigarState) => karigarState.successMessage || null
);

export const selectKarigarAvailability = createSelector(
  [selectKarigarState],
  (karigarState) =>
    karigarState.availability || {
      calendar: [],
      summary: null,
      currentStatus: null,
      capacity: null,
      estimation: null,
      unavailablePeriods: [],
      availableWindows: [],
    }
);

export const selectBulkKarigarAvailability = createSelector(
  [selectKarigarState],
  (karigarState) => {
    const bulkAvailability = karigarState.bulkAvailability;

    if (Array.isArray(bulkAvailability)) {
      return {
        productKarigars: [],
        otherKarigars: [],
        allKarigars: bulkAvailability,
        recommendations: null,
        summary: null,
      };
    }

    if (bulkAvailability && typeof bulkAvailability === "object") {
      return {
        productKarigars: bulkAvailability.productKarigars || [],
        otherKarigars: bulkAvailability.otherKarigars || [],
        allKarigars: bulkAvailability.allKarigars || [],
        recommendations: bulkAvailability.recommendations || null,
        summary: bulkAvailability.summary || null,
      };
    }

    return {
      productKarigars: [],
      otherKarigars: [],
      allKarigars: [],
      recommendations: null,
      summary: null,
    };
  }
);

export const selectBulkProductKarigars = createSelector(
  [selectBulkKarigarAvailability],
  (bulkAvailability) => bulkAvailability.productKarigars || []
);

export const selectBulkOtherKarigars = createSelector(
  [selectBulkKarigarAvailability],
  (bulkAvailability) => bulkAvailability.otherKarigars || []
);

export const selectBulkAllKarigars = createSelector(
  [selectBulkKarigarAvailability],
  (bulkAvailability) => bulkAvailability.allKarigars || []
);

export const selectBulkRecommendations = createSelector(
  [selectBulkKarigarAvailability],
  (bulkAvailability) => bulkAvailability.recommendations || null
);

export const selectBulkSummary = createSelector(
  [selectBulkKarigarAvailability],
  (bulkAvailability) => bulkAvailability.summary || null
);

export const selectKarigarStats = createSelector(
  [selectKarigarState],
  (karigarState) => karigarState.stats || null
);

export const selectAvailableKarigarsFromKarigarState = createSelector(
  [selectKarigarState],
  (karigarState) => {
    const available = karigarState.availableKarigars;
    return Array.isArray(available) ? available : [];
  }
);

export const selectPerformanceReport = createSelector(
  [selectKarigarState],
  (karigarState) => karigarState.performanceReport || null
);

export const selectPasswordStatus = createSelector(
  [selectKarigarState],
  (karigarState) => karigarState.passwordStatus || null
);

export const selectEstimatedHours = createSelector(
  [selectKarigarState],
  (karigarState) => karigarState.estimatedHours || null
);

export const selectKarigarById = (karigarId) =>
  createSelector(
    [selectKarigars],
    (karigars) => karigars.find((k) => k._id === karigarId) || null
  );

export const selectActiveKarigars = createSelector(
  [selectKarigars],
  (karigars) => karigars.filter((k) => k.status === "active")
);

export const selectKarigarsByStatus = (status) =>
  createSelector([selectKarigars], (karigars) =>
    karigars.filter((k) => k.status === status)
  );

// ========== JOB SELECTORS ==========

export const selectJobs = createSelector([selectJobState], (jobState) => {
  const jobs = jobState.jobs;
  if (Array.isArray(jobs)) {
    return jobs;
  }
  if (jobs?.data && Array.isArray(jobs.data)) {
    return jobs.data;
  }
  return [];
});

export const selectCurrentJob = createSelector(
  [selectJobState],
  (jobState) => jobState.currentJob || null
);

export const selectJobPagination = createSelector(
  [selectJobState],
  (jobState) =>
    jobState.pagination || {
      page: 1,
      limit: 20,
      total: 0,
      pages: 0,
    }
);

export const selectJobFilters = createSelector(
  [selectJobState],
  (jobState) =>
    jobState.filters || {
      status: "",
      karigarId: "",
      branchCode: "",
      priority: "",
      jobType: "",
      workType: "",
      orderId: "",
      search: "",
      dateFrom: "",
      dateTo: "",
      isOverdue: "",
    }
);

export const selectJobLoading = createSelector(
  [selectJobState],
  (jobState) =>
    jobState.loading || {
      list: false,
      detail: false,
      create: false,
      update: false,
      status: false,
      assign: false,
      reassign: false,
      progress: false,
      complete: false,
      qualityCheck: false,
      approve: false,
      reject: false,
      rework: false,
      material: false,
      materialReturn: false,
      cancel: false,
      bulkCancel: false,
      stats: false,
      timeline: false,
      availableKarigars: false,
      orderStatus: false,
      karigarQueue: false,
      bulkAssign: false,
      jobTypes: false,
      jobMaterials: false,
      jobPayments: false,
      pricingPreview: false,
      pricing: false,
      payment: false,
      deduction: false,
      removeDeduction: false, // NEW
      submitQC: false,
      revision: false,
      deliver: false,
      canFulfill: false,
      message: false,
      // Image loading states
      fetchImages: false,
      uploadReferenceImages: false,
      deleteReferenceImage: false,
      uploadCompletedImages: false,
      deleteCompletedImage: false,
      uploadItemImages: false,
      uploadQCImages: false,
      bulkDeleteImages: false,
      uploadDesignFile: false,
      // Material CRUD loading states
      materialUpdate: false,
      materialDelete: false,
    }
);

export const selectJobError = createSelector(
  [selectJobState],
  (jobState) => jobState.error || null
);

export const selectJobSuccessMessage = createSelector(
  [selectJobState],
  (jobState) => jobState.successMessage || null
);

export const selectAvailableKarigars = createSelector(
  [selectJobState],
  (jobState) =>
    jobState.availableKarigars || {
      productKarigars: [],
      otherKarigars: [],
      allKarigars: [],
      recommendations: null,
      summary: null,
    }
);

export const selectJobStats = createSelector(
  [selectJobState],
  (jobState) => jobState.stats || null
);

export const selectJobTimeline = createSelector(
  [selectJobState],
  (jobState) => {
    const timeline = jobState.jobTimeline;
    return Array.isArray(timeline) ? timeline : [];
  }
);

export const selectOrderKarigarStatus = createSelector(
  [selectJobState],
  (jobState) => jobState.orderKarigarStatus || null
);

export const selectKarigarQueue = createSelector(
  [selectJobState],
  (jobState) => {
    const queue = jobState.karigarQueue;
    return Array.isArray(queue) ? queue : [];
  }
);

// Select allowed transitions for current job
export const selectAllowedTransitions = createSelector(
  [selectJobState],
  (jobState) => jobState.allowedTransitions || []
);

// Select canBeModified flag for current job
export const selectCanBeModified = createSelector(
  [selectJobState],
  (jobState) => jobState.canBeModified ?? false
);

// Helper selector to check if a specific transition is allowed
export const selectCanTransitionTo = (targetStatus) =>
  createSelector([selectAllowedTransitions], (allowedTransitions) =>
    allowedTransitions.includes(targetStatus)
  );

// Job Types (for dropdowns)
export const selectJobTypes = createSelector(
  [selectJobState],
  (jobState) => jobState.jobTypes || null
);

// Current Job Materials
export const selectCurrentJobMaterials = createSelector(
  [selectJobState],
  (jobState) =>
    jobState.currentJobMaterials || {
      issuedMaterials: [],
      returnedMaterials: [],
      materialSummary: null,
      wastage: null,
      returnRecord: null,
    }
);

// Order Details selector
export const selectOrderDetails = createSelector(
  [selectJobState],
  (jobState) => jobState.orderDetails || null
);

// Product Details selector
export const selectProductDetails = createSelector(
  [selectJobState],
  (jobState) => jobState.productDetails || null
);

// ========== CURRENT JOB PAYMENTS (Updated Structure) ==========

// Full payment state for current job
export const selectCurrentJobPayments = createSelector(
  [selectJobState],
  (jobState) =>
    jobState.currentJobPayments || {
      job: null,
      pricing: null,
      karigarPayment: null,
      payments: {
        completed: [],
        pending: [],
      },
      materialValue: 0,
      wastageValue: 0,
    }
);

// Job reference from payment fetch
export const selectCurrentJobPaymentJob = createSelector(
  [selectCurrentJobPayments],
  (payments) => payments.job || null
);

// Pricing info
export const selectJobPricing = createSelector(
  [selectCurrentJobPayments],
  (payments) => payments.pricing || null
);

// Karigar payment summary (totals, payable, pending, etc.)
export const selectKarigarPaymentInfo = createSelector(
  [selectCurrentJobPayments],
  (payments) => payments.karigarPayment || null
);

// All payments object with completed and pending arrays
export const selectJobPaymentsObject = createSelector(
  [selectCurrentJobPayments],
  (payments) =>
    payments.payments || {
      completed: [],
      pending: [],
    }
);

// Completed payments list
export const selectCompletedJobPayments = createSelector(
  [selectJobPaymentsObject],
  (payments) => payments.completed || []
);

// Pending payments list
export const selectPendingJobPayments = createSelector(
  [selectJobPaymentsObject],
  (payments) => payments.pending || []
);

// All payments combined (for backward compatibility)
export const selectJobPaymentsList = createSelector(
  [selectCompletedJobPayments, selectPendingJobPayments],
  (completed, pending) => [...completed, ...pending]
);

// Deductions from pricing
export const selectJobDeductions = createSelector(
  [selectJobPricing],
  (pricing) => pricing?.deductions || []
);

// Total deductions amount
export const selectTotalDeductionsAmount = createSelector(
  [selectJobDeductions],
  (deductions) => deductions.reduce((sum, d) => sum + (d.amount || 0), 0)
);

// Material value
export const selectJobMaterialValue = createSelector(
  [selectCurrentJobPayments],
  (payments) => payments.materialValue || 0
);

// Wastage value
export const selectJobWastageValue = createSelector(
  [selectCurrentJobPayments],
  (payments) => payments.wastageValue || 0
);

// ========== KARIGAR PAYMENT DERIVED SELECTORS ==========

// Total payable to karigar
export const selectKarigarTotalPayable = createSelector(
  [selectKarigarPaymentInfo],
  (karigarPayment) => karigarPayment?.totalPayable || 0
);

// Total paid to karigar
export const selectKarigarTotalPaid = createSelector(
  [selectKarigarPaymentInfo],
  (karigarPayment) => karigarPayment?.totalPaid || 0
);

// Pending amount (totalPayable - totalPaid)
export const selectKarigarPendingAmount = createSelector(
  [selectKarigarPaymentInfo],
  (karigarPayment) => karigarPayment?.pendingAmount || 0
);

// Payment status (UNPAID, PARTIAL, PAID)
export const selectKarigarPaymentStatus = createSelector(
  [selectKarigarPaymentInfo],
  (karigarPayment) => karigarPayment?.paymentStatus || "UNPAID"
);

// Commission amount
export const selectKarigarCommissionAmount = createSelector(
  [selectKarigarPaymentInfo],
  (karigarPayment) => karigarPayment?.commissionAmount || 0
);

// Commission rate
export const selectKarigarCommissionRate = createSelector(
  [selectKarigarPaymentInfo],
  (karigarPayment) => karigarPayment?.commissionRate || 0
);

// Gross amount (before deductions)
export const selectKarigarGrossAmount = createSelector(
  [selectKarigarPaymentInfo],
  (karigarPayment) => karigarPayment?.grossAmount || 0
);

// Net amount (after deductions)
export const selectKarigarNetAmount = createSelector(
  [selectKarigarPaymentInfo],
  (karigarPayment) => karigarPayment?.netAmount || 0
);

// Is fully paid
export const selectIsJobFullyPaid = createSelector(
  [selectKarigarPaymentStatus],
  (status) => status === "PAID"
);

// Is partially paid
export const selectIsJobPartiallyPaid = createSelector(
  [selectKarigarPaymentStatus],
  (status) => status === "PARTIAL"
);

// Has any payments
export const selectJobHasPayments = createSelector(
  [selectCompletedJobPayments],
  (completedPayments) => completedPayments.length > 0
);

// Has pending payments
export const selectJobHasPendingPayments = createSelector(
  [selectPendingJobPayments],
  (pendingPayments) => pendingPayments.length > 0
);

// Count of completed payments
export const selectCompletedPaymentsCount = createSelector(
  [selectCompletedJobPayments],
  (completedPayments) => completedPayments.length
);

// Count of pending payments
export const selectPendingPaymentsCount = createSelector(
  [selectPendingJobPayments],
  (pendingPayments) => pendingPayments.length
);

// Total amount in pending payments
export const selectPendingPaymentsTotal = createSelector(
  [selectPendingJobPayments],
  (pendingPayments) =>
    pendingPayments.reduce((sum, p) => sum + (p.netAmount || p.amount || 0), 0)
);

// ========== PAYMENT LOADING SELECTORS ==========

// Is payment being recorded
export const selectIsRecordingPayment = createSelector(
  [selectJobLoading],
  (loading) => loading.payment || false
);

// Is pricing being updated
export const selectIsUpdatingPricing = createSelector(
  [selectJobLoading],
  (loading) => loading.pricing || false
);

// Is deduction being added
export const selectIsAddingDeduction = createSelector(
  [selectJobLoading],
  (loading) => loading.deduction || false
);

// Is deduction being removed (NEW)
export const selectIsRemovingDeduction = createSelector(
  [selectJobLoading],
  (loading) => loading.removeDeduction || false
);

// Is fetching job payments
export const selectIsFetchingJobPayments = createSelector(
  [selectJobLoading],
  (loading) => loading.jobPayments || false
);

// Is any payment operation loading
export const selectIsAnyPaymentOperationLoading = createSelector(
  [selectJobLoading],
  (loading) =>
    loading.payment ||
    loading.pricing ||
    loading.deduction ||
    loading.removeDeduction ||
    loading.jobPayments ||
    false
);

// Current Job Images
export const selectCurrentJobImages = createSelector(
  [selectJobState],
  (jobState) =>
    jobState.currentJobImages || {
      referenceImages: [],
      itemImages: [],
      completedImages: [],
      qcImages: [],
      designFile: null,
    }
);

// Individual Image Type Selectors
export const selectReferenceImages = createSelector(
  [selectCurrentJobImages],
  (images) => images.referenceImages || []
);

export const selectItemImages = createSelector(
  [selectCurrentJobImages],
  (images) => images.itemImages || []
);

export const selectCompletedImages = createSelector(
  [selectCurrentJobImages],
  (images) => images.completedImages || []
);

export const selectQCImages = createSelector(
  [selectCurrentJobImages],
  (images) => images.qcImages || []
);

export const selectDesignFile = createSelector(
  [selectCurrentJobImages],
  (images) => images.designFile || null
);

// Total image count selector
export const selectTotalImageCount = createSelector(
  [selectCurrentJobImages],
  (images) => {
    const refCount = images.referenceImages?.length || 0;
    const itemCount = images.itemImages?.length || 0;
    const completedCount = images.completedImages?.length || 0;
    const qcCount = images.qcImages?.length || 0;
    const designCount = images.designFile ? 1 : 0;
    return refCount + itemCount + completedCount + qcCount + designCount;
  }
);

// Image loading state selectors
export const selectIsUploadingImages = createSelector(
  [selectJobLoading],
  (loading) =>
    loading.uploadReferenceImages ||
    loading.uploadCompletedImages ||
    loading.uploadItemImages ||
    loading.uploadQCImages ||
    loading.uploadDesignFile ||
    false
);

export const selectIsDeletingImages = createSelector(
  [selectJobLoading],
  (loading) =>
    loading.deleteReferenceImage ||
    loading.deleteCompletedImage ||
    loading.bulkDeleteImages ||
    false
);

export const selectIsFetchingImages = createSelector(
  [selectJobLoading],
  (loading) => loading.fetchImages || false
);

// Order Karigar Work Status (from order service)
export const selectOrderKarigarWorkStatus = createSelector(
  [selectJobState],
  (jobState) => jobState.orderKarigarWorkStatus || null
);

// Can Fulfill Check
export const selectCanFulfillCheck = createSelector(
  [selectJobState],
  (jobState) => jobState.canFulfillCheck || null
);

// Job by ID
export const selectJobById = (jobId) =>
  createSelector(
    [selectJobs],
    (jobs) => jobs.find((j) => j._id === jobId) || null
  );

// Jobs by status
export const selectJobsByStatus = (status) =>
  createSelector([selectJobs], (jobs) =>
    jobs.filter((j) => j.status === status)
  );

// Product karigars (shown first in assignment)
export const selectProductKarigarsForAssignment = createSelector(
  [selectAvailableKarigars],
  (availableKarigars) => {
    const productKarigars = availableKarigars.productKarigars;
    return Array.isArray(productKarigars) ? productKarigars : [];
  }
);

// Other karigars for assignment
export const selectOtherKarigarsForAssignment = createSelector(
  [selectAvailableKarigars],
  (availableKarigars) => {
    const otherKarigars = availableKarigars.otherKarigars;
    return Array.isArray(otherKarigars) ? otherKarigars : [];
  }
);

// All karigars for assignment (product first, then others)
export const selectAllKarigarsForAssignment = createSelector(
  [selectAvailableKarigars],
  (availableKarigars) => {
    const allKarigars = availableKarigars.allKarigars;
    return Array.isArray(allKarigars) ? allKarigars : [];
  }
);

// Karigar recommendations
export const selectKarigarRecommendations = createSelector(
  [selectAvailableKarigars],
  (availableKarigars) => availableKarigars.recommendations || null
);

// Available karigars summary
export const selectAvailableKarigarsSummary = createSelector(
  [selectAvailableKarigars],
  (availableKarigars) => availableKarigars.summary || null
);

// ========== MATERIAL SELECTORS ==========

export const selectIssuedMaterials = createSelector(
  [selectCurrentJobMaterials],
  (materials) => materials.issuedMaterials || []
);

export const selectReturnedMaterials = createSelector(
  [selectCurrentJobMaterials],
  (materials) => materials.returnedMaterials || []
);

export const selectMaterialSummary = createSelector(
  [selectCurrentJobMaterials],
  (materials) => materials.materialSummary || null
);

export const selectWastage = createSelector(
  [selectCurrentJobMaterials],
  (materials) => materials.wastage || null
);

// Material return record (single object)
export const selectMaterialReturnRecord = createSelector(
  [selectCurrentJobMaterials],
  (materials) => materials.returnRecord || null
);

// Material permissions from backend
export const selectMaterialPermissions = createSelector(
  [selectJobState],
  (jobState) =>
    jobState.materialPermissions || {
      canIssueMaterial: false,
      canRecordReturn: false,
      canEditMaterials: false,
    }
);

// Individual permission selectors
export const selectCanIssueMaterial = createSelector(
  [selectMaterialPermissions],
  (permissions) => permissions.canIssueMaterial || false
);

export const selectCanRecordReturn = createSelector(
  [selectMaterialPermissions],
  (permissions) => permissions.canRecordReturn || false
);

export const selectCanEditMaterials = createSelector(
  [selectMaterialPermissions],
  (permissions) => permissions.canEditMaterials || false
);

// Material CRUD loading state selectors
export const selectIsMaterialUpdating = createSelector(
  [selectJobLoading],
  (loading) => loading.materialUpdate || false
);

export const selectIsMaterialDeleting = createSelector(
  [selectJobLoading],
  (loading) => loading.materialDelete || false
);

export const selectIsMaterialReturning = createSelector(
  [selectJobLoading],
  (loading) => loading.materialReturn || false
);

export const selectIsAnyMaterialLoading = createSelector(
  [selectJobLoading],
  (loading) =>
    loading.material ||
    loading.materialReturn ||
    loading.materialUpdate ||
    loading.materialDelete ||
    loading.jobMaterials ||
    false
);

// Check if materials have been returned
export const selectHasMaterialsReturned = createSelector(
  [selectCurrentJobMaterials],
  (materials) => {
    const returnRecord = materials.returnRecord;
    return !!(returnRecord && returnRecord.finishedWeight > 0);
  }
);

// Get count of materials received by karigar
export const selectReceivedMaterialsCount = createSelector(
  [selectIssuedMaterials],
  (issuedMaterials) => issuedMaterials.filter((m) => m.receivedByKarigar).length
);

// Get materials pending receipt
export const selectPendingReceiptMaterials = createSelector(
  [selectIssuedMaterials],
  (issuedMaterials) => issuedMaterials.filter((m) => !m.receivedByKarigar)
);

// Get materials that have been received
export const selectReceivedMaterials = createSelector(
  [selectIssuedMaterials],
  (issuedMaterials) => issuedMaterials.filter((m) => m.receivedByKarigar)
);

// Get total issued weight by metal type
export const selectTotalIssuedWeightByMetal = createSelector(
  [selectIssuedMaterials],
  (issuedMaterials) => {
    const totals = {};
    issuedMaterials.forEach((m) => {
      const metal = m.metalType || "unknown";
      if (!totals[metal]) {
        totals[metal] = 0;
      }
      totals[metal] += m.weight || 0;
    });
    return totals;
  }
);

// ========== PAYMENT STATE SELECTORS ==========

export const selectPayments = createSelector(
  [selectPaymentState],
  (paymentState) => {
    const payments = paymentState.payments;
    if (Array.isArray(payments)) {
      return payments;
    }
    if (payments?.data && Array.isArray(payments.data)) {
      return payments.data;
    }
    return [];
  }
);

export const selectCurrentPayment = createSelector(
  [selectPaymentState],
  (paymentState) => paymentState.currentPayment || null
);

export const selectPaymentPagination = createSelector(
  [selectPaymentState],
  (paymentState) =>
    paymentState.pagination || {
      page: 1,
      limit: 20,
      total: 0,
      pages: 0,
    }
);

export const selectPaymentFilters = createSelector(
  [selectPaymentState],
  (paymentState) =>
    paymentState.filters || {
      status: "",
      karigarId: "",
      jobId: "",
      paymentType: "",
      dateFrom: "",
      dateTo: "",
      branchCode: "",
    }
);

export const selectPaymentLoading = createSelector(
  [selectPaymentState],
  (paymentState) =>
    paymentState.loading || {
      list: false,
      detail: false,
      create: false,
      update: false,
      approve: false,
      reject: false,
      process: false,
      hold: false,
      release: false,
      cancel: false, // NEW
      pending: false,
      generate: false,
      bulkProcess: false,
      stats: false,
      report: false,
      earnings: false,
      advance: false,
      deduction: false,
      calculate: false,
      export: false,
    }
);

export const selectPaymentError = createSelector(
  [selectPaymentState],
  (paymentState) => paymentState.error || null
);

export const selectPaymentSuccessMessage = createSelector(
  [selectPaymentState],
  (paymentState) => paymentState.successMessage || null
);

export const selectPendingPayments = createSelector(
  [selectPaymentState],
  (paymentState) => {
    const pendingPayments = paymentState.pendingPayments;
    return Array.isArray(pendingPayments) ? pendingPayments : [];
  }
);

export const selectPendingPaymentCount = createSelector(
  [selectPaymentState],
  (paymentState) => paymentState.pendingCount || 0
);

export const selectPendingPaymentTotal = createSelector(
  [selectPaymentState],
  (paymentState) => paymentState.pendingTotal || 0
);

export const selectPaymentStats = createSelector(
  [selectPaymentState],
  (paymentState) => paymentState.stats || null
);

export const selectPaymentReport = createSelector(
  [selectPaymentState],
  (paymentState) => paymentState.report || null
);

export const selectKarigarEarnings = createSelector(
  [selectPaymentState],
  (paymentState) => paymentState.karigarEarnings || null
);

export const selectJobPaymentCalculation = createSelector(
  [selectPaymentState],
  (paymentState) => paymentState.jobPaymentCalculation || null
);

export const selectPaymentById = (paymentId) =>
  createSelector(
    [selectPayments],
    (payments) => payments.find((p) => p._id === paymentId) || null
  );

export const selectPaymentsByStatus = (status) =>
  createSelector([selectPayments], (payments) =>
    payments.filter((p) => p.status === status)
  );

// ========== PAYMENT ACTION LOADING SELECTORS ==========

// Is payment being created
export const selectIsCreatingPayment = createSelector(
  [selectPaymentLoading],
  (loading) => loading.create || false
);

// Is payment being processed
export const selectIsProcessingPayment = createSelector(
  [selectPaymentLoading],
  (loading) => loading.process || false
);

// Is payment being cancelled (NEW)
export const selectIsCancellingPayment = createSelector(
  [selectPaymentLoading],
  (loading) => loading.cancel || false
);

// Is payment being approved
export const selectIsApprovingPayment = createSelector(
  [selectPaymentLoading],
  (loading) => loading.approve || false
);

// Is payment being rejected
export const selectIsRejectingPayment = createSelector(
  [selectPaymentLoading],
  (loading) => loading.reject || false
);

// Is payment being held
export const selectIsHoldingPayment = createSelector(
  [selectPaymentLoading],
  (loading) => loading.hold || false
);

// Is payment being released
export const selectIsReleasingPayment = createSelector(
  [selectPaymentLoading],
  (loading) => loading.release || false
);

// Is bulk processing
export const selectIsBulkProcessing = createSelector(
  [selectPaymentLoading],
  (loading) => loading.bulkProcess || false
);

// Is any payment action loading
export const selectIsAnyPaymentActionLoading = createSelector(
  [selectPaymentLoading],
  (loading) =>
    loading.create ||
    loading.process ||
    loading.cancel ||
    loading.approve ||
    loading.reject ||
    loading.hold ||
    loading.release ||
    loading.bulkProcess ||
    false
);

// ========== COMBINED LOADING SELECTOR ==========
export const selectAnyLoading = createSelector(
  [selectKarigarLoading, selectJobLoading, selectPaymentLoading],
  (karigarLoading, jobLoading, paymentLoading) => {
    return (
      Object.values(karigarLoading).some(Boolean) ||
      Object.values(jobLoading).some(Boolean) ||
      Object.values(paymentLoading).some(Boolean)
    );
  }
);

// ========== COMBINED ERROR SELECTOR ==========
export const selectAnyError = createSelector(
  [selectKarigarError, selectJobError, selectPaymentError],
  (karigarError, jobError, paymentError) => {
    return karigarError || jobError || paymentError || null;
  }
);

// ========== PRICING PREVIEW SELECTORS ==========

// Main pricing preview selector
export const selectPricingPreview = createSelector(
  [selectJobState],
  (jobState) => jobState.pricingPreview || null
);

// Current rate type configured for the job
export const selectCurrentRateType = createSelector(
  [selectPricingPreview],
  (preview) => preview?.currentRateType || "PER_GRAM"
);

// Labor rate value
export const selectLaborRate = createSelector(
  [selectPricingPreview],
  (preview) => preview?.laborRate || 0
);

// Commission rate
export const selectCommissionRate = createSelector(
  [selectPricingPreview],
  (preview) => preview?.commissionRate || 0
);

// Available values for calculation (finishedWeight, pieceCount, etc.)
export const selectAvailableValues = createSelector(
  [selectPricingPreview],
  (preview) =>
    preview?.availableValues || {
      finishedWeight: 0,
      pieceCount: 1,
      materialValue: 0,
      actualHours: 0,
    }
);

// Current calculation result based on selected rate type
export const selectCurrentCalculation = createSelector(
  [selectPricingPreview],
  (preview) => preview?.currentCalculation || null
);

// All rate type previews (PER_GRAM, FIXED, PERCENTAGE, PER_PIECE, HOURLY)
export const selectAllRateTypePreviews = createSelector(
  [selectPricingPreview],
  (preview) => preview?.allRateTypePreviews || {}
);

// Can calculate pricing flag
export const selectCanCalculatePricing = createSelector(
  [selectPricingPreview],
  (preview) => preview?.canCalculate || false
);

// Missing fields for calculation
export const selectMissingPricingFields = createSelector(
  [selectPricingPreview],
  (preview) => preview?.missingFields || []
);

// Ready fields for calculation
export const selectReadyPricingFields = createSelector(
  [selectPricingPreview],
  (preview) => preview?.readyFields || {}
);

// Stored pricing from job
export const selectStoredPricing = createSelector(
  [selectPricingPreview],
  (preview) => preview?.storedPricing || null
);

// Recommendation message
export const selectPricingRecommendation = createSelector(
  [selectPricingPreview],
  (preview) => preview?.recommendation || null
);

// Pricing preview loading state
export const selectPricingPreviewLoading = createSelector(
  [selectJobLoading],
  (loading) => loading.pricingPreview || false
);

// Get preview for specific rate type
export const selectRateTypePreview = (rateType) =>
  createSelector(
    [selectAllRateTypePreviews],
    (previews) => previews[rateType] || null
  );

export const selectTotalAdditionalCharges = createSelector(
  [selectJobPricing],
  (pricing) =>
    pricing?.additionalCharges?.reduce((sum, c) => sum + (c.amount || 0), 0) ||
    0
);

// Check if specific rate type has required data
export const selectRateTypeHasData = (rateType) =>
  createSelector(
    [selectAllRateTypePreviews],
    (previews) => previews[rateType]?.hasRequiredData || false
  );

// ========== PAYMENT SUMMARY SELECTOR ==========

// Comprehensive payment summary for job detail view
export const selectJobPaymentSummary = createSelector(
  [
    selectKarigarPaymentInfo,
    selectCompletedJobPayments,
    selectPendingJobPayments,
    selectJobDeductions,
    selectJobMaterialValue,
    selectJobWastageValue,
  ],
  (
    karigarPayment,
    completedPayments,
    pendingPayments,
    deductions,
    materialValue,
    wastageValue
  ) => ({
    // Amounts
    grossAmount: karigarPayment?.grossAmount || 0,
    commissionAmount: karigarPayment?.commissionAmount || 0,
    commissionRate: karigarPayment?.commissionRate || 0,
    totalDeductions: deductions.reduce((sum, d) => sum + (d.amount || 0), 0),
    netPayable: karigarPayment?.totalPayable || 0,
    totalPaid: karigarPayment?.totalPaid || 0,
    pendingAmount: karigarPayment?.pendingAmount || 0,

    // Material info
    materialValue,
    wastageValue,

    // Status
    paymentStatus: karigarPayment?.paymentStatus || "UNPAID",
    isFullyPaid: karigarPayment?.paymentStatus === "PAID",
    isPartiallyPaid: karigarPayment?.paymentStatus === "PARTIAL",

    // Counts
    completedPaymentsCount: completedPayments.length,
    pendingPaymentsCount: pendingPayments.length,
    deductionsCount: deductions.length,

    // Lists
    completedPayments,
    pendingPayments,
    deductions,
  })
);
